import { useEffect, useState } from 'react';
import {
  PhieuYeuCauXuatKho,
  PhieuYeuCauXuatKhoDetail,
  PhieuYeuCauXuatKhoInput,
  PhieuYeuCauXuatKhoResponse
} from '@/types/schemas/phieu-yeu-cau-xuat-kho.type';
import { QUERY_KEYS } from '@/constants/query-keys';
import type { ApiResponse } from '@/types/api.type';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

interface UsePhieuYeuCauXuatKhoReturn {
  phieuYeuCauXuatKhos: PhieuYeuCauXuatKho[];
  isLoading: boolean;
  isLoadingDetail: boolean;
  addPhieuYeuCauXuatKho: (newPhieuYeuCauXuatKho: PhieuYeuCauXuatKhoInput) => Promise<PhieuYeuCauXuatKho>;
  updatePhieuYeuCauXuatKho: (
    uuid: string,
    updatedPhieuYeuCauXuatKho: PhieuYeuCauXuatKhoInput
  ) => Promise<PhieuYeuCauXuatKho>;
  deletePhieuYeuCauXuatKho: (uuid: string) => Promise<void>;
  refreshPhieuYeuCauXuatKhos: () => Promise<void>;
  getPhieuYeuCauXuatKhoByCustomer: (customerId: string) => Promise<PhieuYeuCauXuatKho[]>;
  getPhieuYeuCauXuatKhoDetail: (
    uuid: string
  ) => Promise<PhieuYeuCauXuatKhoDetail[] | ApiResponse<PhieuYeuCauXuatKhoDetail>>;
}

/**
 * Hook for managing PhieuYeuCauXuatKho (Inventory Check Request) data
 *
 * This hook provides functions to fetch, create, update, and delete sales invoices and their details.
 */
export const usePhieuYeuCauXuatKho = (
  initialPhieuYeuCauXuatKhos: PhieuYeuCauXuatKho[] = []
): UsePhieuYeuCauXuatKhoReturn => {
  const [phieuYeuCauXuatKhos, setPhieuYeuCauXuatKhos] = useState<PhieuYeuCauXuatKho[]>(initialPhieuYeuCauXuatKhos);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(true);

  const { entity } = useAuth();

  const fetchPhieuYeuCauXuatKhos = async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get<PhieuYeuCauXuatKhoResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_YEU_CAU_XUAT_KHO}/`
      );
      setPhieuYeuCauXuatKhos(response.data.results);
    } catch (error) {
      console.error('Error fetching sales invoices:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getPhieuYeuCauXuatKhoByCustomer = async (customerId: string): Promise<PhieuYeuCauXuatKho[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<PhieuYeuCauXuatKhoResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_YEU_CAU_XUAT_KHO}/?ma_kh=${customerId}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching sales invoices by customer:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const addPhieuYeuCauXuatKho = async (newPhieuYeuCauXuatKho: PhieuYeuCauXuatKhoInput): Promise<PhieuYeuCauXuatKho> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      const response = await api.post<PhieuYeuCauXuatKho>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_YEU_CAU_XUAT_KHO}/`,
        newPhieuYeuCauXuatKho
      );

      const addedPhieuYeuCauXuatKho = response.data;
      setPhieuYeuCauXuatKhos(prev => [...prev, addedPhieuYeuCauXuatKho]);
      return addedPhieuYeuCauXuatKho;
    } catch (error) {
      console.error('Error adding sales invoice:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updatePhieuYeuCauXuatKho = async (
    uuid: string,
    updatedPhieuYeuCauXuatKho: PhieuYeuCauXuatKhoInput
  ): Promise<PhieuYeuCauXuatKho> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      const response = await api.patch<PhieuYeuCauXuatKho>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_YEU_CAU_XUAT_KHO}/${uuid}/`,
        updatedPhieuYeuCauXuatKho
      );

      const updatedData = response.data;
      setPhieuYeuCauXuatKhos(prev => prev.map(item => (item.uuid === uuid ? updatedData : item)));
      return updatedData;
    } catch (error) {
      console.error('Error updating sales invoice:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deletePhieuYeuCauXuatKho = async (uuid: string) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_YEU_CAU_XUAT_KHO}/${uuid}/`);
      setPhieuYeuCauXuatKhos(prev => prev.filter(item => item.uuid !== uuid));
    } catch (error) {
      console.error('Error deleting sales invoice:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getPhieuYeuCauXuatKhoDetail = async (
    uuid: string
  ): Promise<PhieuYeuCauXuatKhoDetail[] | ApiResponse<PhieuYeuCauXuatKhoDetail>> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoadingDetail(true);
    try {
      const response = await api.get<PhieuYeuCauXuatKhoDetail[] | ApiResponse<PhieuYeuCauXuatKhoDetail>>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_YEU_CAU_XUAT_KHO}/${uuid}/chi-tiet/`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching sales invoice detail:', error);
      throw error;
    } finally {
      setIsLoadingDetail(false);
    }
  };

  useEffect(() => {
    fetchPhieuYeuCauXuatKhos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity?.slug]);

  return {
    phieuYeuCauXuatKhos,
    isLoading,
    isLoadingDetail,
    addPhieuYeuCauXuatKho,
    updatePhieuYeuCauXuatKho,
    deletePhieuYeuCauXuatKho,
    refreshPhieuYeuCauXuatKhos: fetchPhieuYeuCauXuatKhos,
    getPhieuYeuCauXuatKhoByCustomer,
    getPhieuYeuCauXuatKhoDetail
  };
};
